import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useJWTAuth } from '../components/JWTAuthContext';
import { API_BASE_URL, getUserSubscriptionFeatures } from '../components/apiClient';
import PricingCard from '../components/shared/PricingCard';
import '../styles/SubscriptionPlans.css';

interface Feature {
  feature_id: string;
  feature_key: string;
  feature_name: string;
  feature_description: string;
  feature_type: 'quota' | 'boolean' | 'credits';
  reset_cycle: string;
  feature_limit: string | null;
  is_enabled: boolean;
}

interface Plan {
  id: string;
  plan_key: string;
  plan_name: string;
  plan_description: string;
  monthly_price_inr: string;
  academic_session_price_inr: string;
  is_free: boolean;
  is_default: boolean;
  plan_color: string;
  is_popular?: boolean;
  features: Feature[];
}

interface SubscriptionPlansResponse {
  status: string;
  result: Plan[];
  message: string;
}

interface UserSubscription {
  subscription_plan_name: string;
  subscription_plan_key: string;
  subscription_status: string;
  subscription_expiry_date?: string;
}

const SubscriptionPlans: React.FC = () => {
  const { isAuthenticated, user } = useJWTAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'academic'>('monthly');
  const [userSubscription, setUserSubscription] = useState<UserSubscription | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch plans first
      const plansResponse = await fetch(`${API_BASE_URL}/subscription/plans`);
      const plansData: SubscriptionPlansResponse = await plansResponse.json();
      
      if (plansData.status === 'success') {
        // Mark Premium plan as popular (best seller)
        const updatedPlans = plansData.result.map(plan => ({
          ...plan,
          is_popular: plan.plan_key === 'premium'
        }));
        setPlans(updatedPlans);
      } else {
        setError('Failed to load subscription plans');
        setLoading(false);
        return;
      }

      // If user is authenticated, fetch their subscription
      if (isAuthenticated) {
        try {
          const subscriptionResponse = await getUserSubscriptionFeatures();
          
          // API returns status as "success" string, not boolean
          // Data is in 'result' field
          if (subscriptionResponse && 
              (subscriptionResponse.status === 'success' || subscriptionResponse.status === true) && 
              subscriptionResponse.result) {
            const userSub = {
              subscription_plan_name: subscriptionResponse.result.subscription_plan_name,
              subscription_plan_key: subscriptionResponse.result.subscription_plan_key,
              subscription_status: subscriptionResponse.result.subscription_status,
              subscription_expiry_date: subscriptionResponse.result.expires_at
            };
            setUserSubscription(userSub);
          }
        } catch (err) {
          console.error('Error fetching user subscription:', err);
          // Don't show error, just fail silently and show plans without highlighting
        }
      }
      
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load subscription plans. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const isCurrentPlan = (planKey: string): boolean => {
    // Only show current plan if user is authenticated and has subscription data
    if (!isAuthenticated || !userSubscription) return false;
    return userSubscription.subscription_plan_key === planKey;
  };

  const getPlanHierarchyLevel = (planKey: string): number => {
    const hierarchy: { [key: string]: number } = {
      'free': 0,
      'basic': 1,
      'premium': 2,
      'pro': 3
    };
    return hierarchy[planKey.toLowerCase()] ?? 0;
  };

  const isLowerThanCurrentPlan = (planKey: string): boolean => {
    // Only evaluate plan hierarchy if user is authenticated
    if (!isAuthenticated || !userSubscription) return false;
    
    const currentPlanLevel = getPlanHierarchyLevel(userSubscription.subscription_plan_key);
    const thisPlanLevel = getPlanHierarchyLevel(planKey);
    
    // Don't disable free plan
    if (planKey.toLowerCase() === 'free') return false;
    
    return thisPlanLevel < currentPlanLevel;
  };

  const handlePlanAction = (plan: Plan) => {
    if (!isAuthenticated) {
      // Redirect to signup/login with return URL
      window.location.href = '/signup';
      return;
    }
    
    // Don't redirect for free plan
    if (plan.is_free) {
      alert('You are already on the free plan!');
      return;
    }
    
    // Redirect to payment page with plan details
    navigate('/payment', { 
      state: { 
        plan: plan,
        billingCycle: billingCycle 
      } 
    });
  };

  if (loading) {
    return (
      <div className="subscription-plans-container">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-plans-container">
        <div className="alert alert-danger m-5" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-plans-container">
      {/* Hero Section */}
      <section className="pricing-hero">
        <div className="container text-center">
          <h1 className="display-4 fw-bold mb-3">Choose Your Learning Plan</h1>
          <p className="lead text-white mb-4">
            Unlock your potential with our AI-powered learning platform
          </p>
          
          {/* Billing Cycle Toggle */}
          <div className="billing-toggle mb-5">
            <button
              className={`btn ${billingCycle === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`btn ${billingCycle === 'academic' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setBillingCycle('academic')}
            >
              Academic Session
              <span className="badge bg-success ms-2">Save More</span>
            </button>
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section className="pricing-plans py-5">
        <div className="container">
          <div className="row g-4">
            {plans.map((plan) => {
              const isUserCurrentPlan = isCurrentPlan(plan.plan_key);
              const isLowerPlan = isLowerThanCurrentPlan(plan.plan_key);
              
              return (
                <div key={plan.id} className="col-lg-3 col-md-6">
                  <PricingCard
                    plan={plan}
                    billingCycle={billingCycle}
                    showFeatures={true}
                    variant="default"
                    isCurrentPlan={isUserCurrentPlan}
                    isLowerPlan={isLowerPlan}
                    onActionClick={handlePlanAction}
                    ctaText={isUserCurrentPlan ? 'Current Plan' : undefined}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pricing-faq py-5 bg-light">
        <div className="container">
          <h2 className="text-center mb-5">Frequently Asked Questions</h2>
          <div className="row">
            <div className="col-md-6 mb-4">
              <div className="faq-item">
                <h5 className="fw-bold mb-3">
                  <i className="fas fa-question-circle text-primary me-2"></i>
                  Can I change my plan later?
                </h5>
                <p className="text-muted">
                  Yes! You can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.
                </p>
              </div>
            </div>
            <div className="col-md-6 mb-4">
              <div className="faq-item">
                <h5 className="fw-bold mb-3">
                  <i className="fas fa-question-circle text-primary me-2"></i>
                  What payment methods do you accept?
                </h5>
                <p className="text-muted">
                  We accept all major credit cards, debit cards, UPI, and net banking through our secure payment gateway.
                </p>
              </div>
            </div>
            <div className="col-md-6 mb-4">
              <div className="faq-item">
                <h5 className="fw-bold mb-3">
                  <i className="fas fa-question-circle text-primary me-2"></i>
                  Is there a free trial?
                </h5>
                <p className="text-muted">
                  Yes! Our FREE plan gives you access to basic features. You can upgrade anytime to access premium features.
                </p>
              </div>
            </div>
            <div className="col-md-6 mb-4">
              <div className="faq-item">
                <h5 className="fw-bold mb-3">
                  <i className="fas fa-question-circle text-primary me-2"></i>
                  What happens when I reach my quota limit?
                </h5>
                <p className="text-muted">
                  You can either wait for the next reset cycle or upgrade to a higher plan for more quota or unlimited access.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {!isAuthenticated && (
        <section className="pricing-cta py-5 text-center">
          <div className="container">
            <h2 className="mb-4">Ready to Start Learning?</h2>
            <p className="lead text-muted mb-4">
              Join thousands of students already learning with WiZiAi
            </p>
            <div>
              <Link to="/signup" className="btn btn-primary btn-lg me-3">
                <i className="fas fa-rocket me-2"></i>
                Get Started Free
              </Link>
              <Link to="/contact" className="btn btn-outline-primary btn-lg">
                <i className="fas fa-envelope me-2"></i>
                Contact Sales
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default SubscriptionPlans;
